import { history } from 'instantsearch.js/es/lib/routers';

/**
 * Create history router for URL state management
 * @param {Object} options - Router options
 * @returns {Object} History router instance
 */
export function historyRouter(options = {}) {
  return history({
    cleanUrlOnDispose: false,
    ...options,
  });
}

/**
 * Create simple state mapping for cleaner URLs
 * @param {string} indexName - The index name to map
 * @param {Object} defaultToggleState - Default toggle state from data-checked attributes
 * @param {Object} defaultRefinementState - Default refinement state from data-default-refinement attributes
 * @returns {Object} State mapping object
 */
export function createStateMapping(
  indexName,
  defaultToggleState = {},
  defaultRefinementState = {},
) {
  return {
    stateToRoute(uiState) {
      const indexUiState = uiState[indexName] || {};
      const route = {
        q: indexUiState.query,
        page: indexUiState.page,
      };

      // Add refinement list params, but skip if they match defaults
      Object.entries(indexUiState.refinementList || {}).forEach(
        ([key, value]) => {
          const defaultValue = defaultRefinementState[key];
          const currentSorted = [...value].sort().join(',');
          const defaultSorted = defaultValue
            ? [...defaultValue].sort().join(',')
            : '';

          // Only add to URL if different from default
          if (currentSorted !== defaultSorted) {
            route[key] = value.join(',');
          }
        },
      );

      // Track when default refinements are explicitly cleared
      Object.entries(defaultRefinementState).forEach(([key]) => {
        const currentValue = indexUiState.refinementList?.[key];
        if (!currentValue || currentValue.length === 0) {
          route[`clear_${key}`] = '1';
        }
      });

      // Add range params
      Object.entries(indexUiState.range || {}).forEach(([key, value]) => {
        if (value) {
          route[`${key}_min`] = value.min;
          route[`${key}_max`] = value.max;
        }
      });

      // Only add toggle params to URL if they differ from defaults
      Object.entries(indexUiState.toggle || {}).forEach(([key, value]) => {
        if (value !== defaultToggleState[key]) {
          route[`toggle_${key}`] = value ? '1' : '0';
        }
      });
      // Also track when default toggles are explicitly turned off
      Object.entries(defaultToggleState).forEach(([key]) => {
        const currentValue = indexUiState.toggle?.[key];
        if (currentValue === false) {
          route[`toggle_${key}`] = '0';
        }
      });

      return route;
    },
    routeToState(routeState) {
      // Start with default refinements
      const refinementList = { ...defaultRefinementState };
      const range = {};
      const toggle = { ...defaultToggleState };

      Object.entries(routeState).forEach(([key, value]) => {
        if (key === 'q' || key === 'page') return;

        if (key.startsWith('toggle_')) {
          const attr = key.replace('toggle_', '');
          toggle[attr] = value === '1';
        } else if (key.startsWith('clear_')) {
          // Handle explicitly cleared defaults
          const attr = key.replace('clear_', '');
          delete refinementList[attr];
        } else if (key.endsWith('_min') || key.endsWith('_max')) {
          const attr = key.replace(/_min$|_max$/, '');
          if (!range[attr]) range[attr] = {};
          if (key.endsWith('_min')) range[attr].min = value;
          if (key.endsWith('_max')) range[attr].max = value;
        } else if (value) {
          refinementList[key] = value.split(',');
        }
      });

      return {
        [indexName]: {
          query: routeState.q,
          page: routeState.page,
          refinementList,
          range,
          toggle,
        },
      };
    },
  };
}
